import React, { useState, useEffect, memo } from 'react';
import { makeStyles } from 'tss-react/mui';
import { Grid } from '@mui/material';
import {
  PageTitleAndSubTitle,
  CardComponent,
} from '@HomaHealth/ui-component-library/lib/_AIQ-components';
import Loader from '../../_components/molecules/Loader';

// Temporary type definitions for removed playbook types
// type PlaybookData = any;
// type RegulatoryPlaybookData = any;
// type GapPlaybookData = any;
type PlaybookType = 'standard' | 'regulatory' | 'gap';
// type GapCombination = 'pd-stoploss' | 'asa-stoploss' | string;
import Snackbar from '../../_components/atoms/Snackbar';
import { useParams } from 'react-router-dom';
import { AlertColor as MuiColor } from '@mui/material/Alert';
import { patientAccessTheme } from '@HomaHealth/ui-component-library';

export interface GagClauseCompliancePageProps {}

type RouteParams = {
  type?: string;
};

const useStyles = makeStyles()(() => ({
  root: {
    flexGrow: 1,
    padding: '24px',
    backgroundColor: patientAccessTheme.palette.background.default,
  },
  container: {
    marginBottom: '18px',
    width: '100%',
    maxWidth: '1400px',
    position: 'relative',
  },
  content: {
    padding: '24px',
    backgroundColor: patientAccessTheme.palette.background.paper,
    borderRadius: '8px',
  },
  comparisonSection: {
    marginBottom: '24px',
  },
  comparisonHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '16px',
  },
  addParameterButton: {
    backgroundColor: patientAccessTheme.palette.primary.main,
    color: patientAccessTheme.palette.primary.contrastText,
    textTransform: 'none',
    fontWeight: 500,
    padding: '8px 16px',
    '&:hover': {
      backgroundColor: patientAccessTheme.palette.primary.dark,
    },
  },
  parameterCard: {
    marginBottom: '8px',
    border: `1px solid ${patientAccessTheme.palette.grey[300]}`,
    borderRadius: '4px',
    backgroundColor: patientAccessTheme.palette.background.paper,
    boxShadow: 'none',
    '&:before': {
      display: 'none',
    },
  },
  parameterHeader: {
    padding: '12px 16px',
    minHeight: '48px',
    backgroundColor: patientAccessTheme.palette.grey[50],
    '& .MuiAccordionSummary-content': {
      margin: '8px 0',
      alignItems: 'center',
    },
  },
  parameterContent: {
    padding: '16px',
    borderTop: `1px solid ${patientAccessTheme.palette.grey[200]}`,
  },
  parameterName: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    flex: 1,
  },
  dragHandle: {
    color: patientAccessTheme.palette.grey[400],
    cursor: 'grab',
    marginRight: '8px',
  },
  parameterTitle: {
    fontWeight: 500,
    fontSize: '14px',
    color: patientAccessTheme.palette.text.primary,
  },
  parameterDescription: {
    color: patientAccessTheme.palette.text.secondary,
    fontSize: '13px',
    lineHeight: '1.4',
  },
  actionButtons: {
    display: 'flex',
    gap: '4px',
    marginLeft: '16px',
  },
  actionButton: {
    padding: '4px',
    color: patientAccessTheme.palette.grey[600],
    '&:hover': {
      backgroundColor: patientAccessTheme.palette.grey[100],
    },
  },
  subParameter: {
    marginLeft: '32px',
    marginTop: '8px',
    padding: '12px 16px',
    backgroundColor: patientAccessTheme.palette.grey[50],
    border: `1px solid ${patientAccessTheme.palette.grey[200]}`,
    borderRadius: '4px',
  },
  subParameterHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  addSubParameterButton: {
    color: patientAccessTheme.palette.primary.main,
    textTransform: 'none',
    fontSize: '13px',
    fontWeight: 500,
    padding: '4px 8px',
    marginTop: '8px',
    '&:hover': {
      backgroundColor: patientAccessTheme.palette.primary.light + '20',
    },
  },
  breadcrumbContainer: {
    marginBottom: '24px',
  },
}));

export const GagClauseCompliancePage: React.FC<GagClauseCompliancePageProps> = memo(
  () => {
    const { classes } = useStyles();
    // const history = useHistory();
    const { type } = useParams<RouteParams>();
    const [loading, setLoading] = useState(false);
    // const [saving, setSaving] = useState(false);
    // const [playbookType, setPlaybookType] = useState<PlaybookType>('standard');
    // const [gapCombination, setGapCombination] = useState<GapCombination>(
    //   'pd-stoploss',
    // );

    // // Standard playbook state
    // const [playbookData, setPlaybookData] = useState<PlaybookData>({
    //   prompt_1: '',
    //   prompt_schema: '',
    //   standards: [],
    //   model: 'gpt-4o',
    // });

    // // Regulatory playbook state
    // const [
    //   regulatoryPlaybookData,
    //   setRegulatoryPlaybookData,
    // ] = useState<RegulatoryPlaybookData>({
    //   applicability_helpers: '',
    //   prompt_1: '',
    //   prompt_schema: '',
    //   model: 'gpt-4o',
    //   compliances: [],
    // });

    // // Gap playbook state
    // const [gapPlaybookData, setGapPlaybookData] = useState<GapPlaybookData>({
    //   pd_prompt_1: '',
    //   pd_model: 'gpt-4o',
    //   pd_topics: [],
    //   pd_schema: '',
    //   stoploss_prompt_1: '',
    //   stoploss_model: 'gpt-4o',
    //   stoploss_topics: [],
    //   stoploss_schema: '',
    //   prompt_schema: '',
    // });

    const [snackbarState, setSnackbarState] = useState({
      open: false,
      message: '',
      severity: 'success' as MuiColor,
      autoHideDuration: 3000,
    });

    const handleOpenSnackBar = (
      message: string,
      severity: MuiColor,
      autoHideDuration: number,
    ) => {
      setSnackbarState({
        open: true,
        message,
        severity,
        autoHideDuration,
      });
    };

    const handleCloseSnackBar = () => {
      setSnackbarState((prev) => ({ ...prev, open: false }));
    };

    // Determine playbook type based on route
    const determinePlaybookType = (routeType: string): PlaybookType => {
      if (routeType === 'regulatory') {
        return 'regulatory';
      }
      if (routeType?.includes('gap-analysis')) {
        return 'gap';
      }
      return 'standard';
    };

    // Determine gap combination based on route
    // const determineGapCombination = (routeType: string): GapCombination => {
    //   if (routeType?.includes('pd-stoploss')) {
    //     return 'pd-stoploss';
    //   }
    //   if (routeType?.includes('asa-stoploss')) {
    //     return 'stoploss-asa';
    //   }
    //   if (routeType?.includes('pd-asa')) {
    //     return 'asa-pd';
    //   }
    //   return 'pd-stoploss';
    // };

    const getPlaybookData = async () => {
      setLoading(true);
      try {
        const currentPlaybookType = determinePlaybookType(type || '');
        // setPlaybookType(currentPlaybookType);

        if (currentPlaybookType === 'gap') {
          // setGapCombination(determineGapCombination(type || ''));
          // Load mock gap playbook data
          // setGapPlaybookData({
          //   pd_prompt_1: 'Sample PD prompt for gap analysis',
          //   pd_model: 'gpt-4o',
          //   pd_topics: [
          //     {
          //       id: 1,
          //       name: 'Coverage Requirements',
          //       definition:
          //         'Essential coverage requirements for plan documents',
          //       checkoff_list: [
          //         { id: 1, text: 'Minimum benefits' },
          //         { id: 2, text: 'Exclusions' },
          //         { id: 3, text: 'Cost sharing' },
          //       ],
          //     },
          //   ],
          //   pd_schema: 'Sample PD schema',
          //   stoploss_prompt_1: 'Sample Stoploss prompt for gap analysis',
          //   stoploss_model: 'gpt-4o',
          //   stoploss_topics: [
          //     {
          //       id: 2,
          //       name: 'Risk Management',
          //       definition:
          //         'Risk management requirements for stoploss coverage',
          //       checkoff_list: [
          //         { id: 4, text: 'Specific deductible' },
          //         { id: 5, text: 'Aggregate deductible' },
          //         { id: 6, text: 'Coverage limits' },
          //       ],
          //     },
          //   ],
          //   stoploss_schema: 'Sample Stoploss schema',
          //   prompt_schema: 'Global schema for gap analysis',
          // });
        } else if (currentPlaybookType === 'regulatory') {
          // Load mock regulatory playbook data
          // setRegulatoryPlaybookData({
          //   applicability_helpers:
          //     'Guidelines for determining regulatory applicability',
          //   prompt_1: 'Regulatory compliance analysis prompt',
          //   prompt_schema: 'Schema for regulatory compliance output',
          //   model: 'gpt-4o',
          //   compliances: [
          //     {
          //       id: 1,
          //       number: 1,
          //       name: 'ERISA Compliance',
          //       applicability: 'Applies to all employer-sponsored health plans',
          //       questions: [
          //         { id: 1, text: 'Are fiduciary duties clearly defined?' },
          //         { id: 2, text: 'Is there proper claims administration?' },
          //       ],
          //     },
          //     {
          //       id: 2,
          //       number: 2,
          //       name: 'HIPAA Compliance',
          //       applicability: 'Applies to all covered entities handling PHI',
          //       questions: [
          //         { id: 3, text: 'Are privacy protections in place?' },
          //         { id: 4, text: 'Is there a breach notification process?' },
          //       ],
          //     },
          //   ],
          // });
        } else {
          // Load mock standard playbook data
          // setPlaybookData({
          //   prompt_1: 'Standard gag clause compliance analysis prompt',
          //   prompt_schema: 'Schema for standard compliance output',
          //   model: 'gpt-4o',
          //   standards: [
          //     {
          //       id: 1,
          //       name: 'Fee Structure',
          //       aim:
          //         'Ensure transparency in fee structures and cost components',
          //       checkoff_list: [
          //         { id: 1, text: 'Base fees are clearly defined' },
          //         { id: 2, text: 'Per-participant fees are specified' },
          //         { id: 3, text: 'Per-claim fees are documented' },
          //       ],
          //     },
          //     {
          //       id: 2,
          //       name: 'KPI Requirements',
          //       aim: 'Establish key performance indicators for service quality',
          //       checkoff_list: [
          //         { id: 4, text: 'Cost efficiency metrics' },
          //         { id: 5, text: 'Service quality benchmarks' },
          //         { id: 6, text: 'Financial impact assessments' },
          //       ],
          //     },
          //   ],
          // });
        }
      } catch (error: any) {
        handleOpenSnackBar(
          error.response?.data?.error || 'Failed to fetch playbook data',
          'error',
          6000,
        );
      } finally {
        setLoading(false);
      }
    };

    useEffect(() => {
      getPlaybookData();
    }, [type]);

    // Save handlers for each playbook type
    // const handlePlaybookSave = async (tableData: PlaybookData) => {
    //   setSaving(true);
    //   try {
    //     console.log('Saving standard playbook data:', tableData);
    //     // API call would go here
    //     handleOpenSnackBar(
    //       'Standard playbook saved successfully!',
    //       'success',
    //       3000,
    //     );
    //   } catch (error: any) {
    //     handleOpenSnackBar('Failed to save standard playbook', 'error', 3000);
    //   } finally {
    //     setSaving(false);
    //   }
    // };

    // const handleRegulatoryPlaybookSave = async (
    //   tableData: RegulatoryPlaybookData,
    // ) => {
    //   setSaving(true);
    //   try {
    //     console.log('Saving regulatory playbook data:', tableData);
    //     // API call would go here
    //     handleOpenSnackBar(
    //       'Regulatory playbook saved successfully!',
    //       'success',
    //       3000,
    //     );
    //   } catch (error: any) {
    //     handleOpenSnackBar('Failed to save regulatory playbook', 'error', 3000);
    //   } finally {
    //     setSaving(false);
    //   }
    // };

    // const handleGapPlaybookSave = async (tableData: GapPlaybookData) => {
    //   setSaving(true);
    //   try {
    //     console.log('Saving gap playbook data:', tableData);
    //     // API call would go here
    //     handleOpenSnackBar('Gap playbook saved successfully!', 'success', 3000);
    //   } catch (error: any) {
    //     handleOpenSnackBar('Failed to save gap playbook', 'error', 3000);
    //   } finally {
    //     setSaving(false);
    //   }
    // };

    const getPageTitle = () => {
      switch (type) {
        case 'gag-compliance':
          return 'Gag Clause Compliance';
        case 'regulatory':
          return 'Regulatory Compliance';
        case 'gap-analysis-pd-stoploss':
          return 'Gap Analysis: PD - Stoploss';
        case 'gap-analysis-pd-asa':
          return 'Gap Analysis: PD - ASA';
        case 'gap-analysis-asa-stoploss':
          return 'Gap Analysis: ASA - Stoploss';
        case 'compensation-transparency-red-flag':
          return 'Compensation Transparency Red Flag Analysis';
        case 'compensation-transparency-clause-alignment':
          return 'Compensation Transparency Clause Alignment';
        case 'purchasing-standards':
          return 'Purchasing Standards';
        default:
          return 'Playbook Configuration';
      }
    };

    // const breadcrumbItems = [
    //   {
    //     label: 'Compliance Assurance',
    //     onClick: () => history.push('/compliance-assurance'),
    //   },
    //   { label: getPageTitle() },
    // ];

    if (loading) {
      return <Loader />;
    }

    return (
      <>
        <Grid container className={classes.root}>
          <Grid className={classes.container} size={12}>
            <PageTitleAndSubTitle
              title={getPageTitle()}
              subTitle="Configure and edit playbook parameters"
            />
          </Grid>

          <Grid className={classes.container} size={12}>
            <CardComponent
              title=""
              subTitle=""
              width="100%"
              height="auto"
              body={
                <div style={{ padding: '24px', textAlign: 'center' }}>
                  <p>Playbook display functionality has been removed.</p>
                </div>
              }
            />
          </Grid>

          <div id="scroll-top-element" />
        </Grid>
        <Snackbar
          {...snackbarState}
          handleClose={handleCloseSnackBar}
          vertical="bottom"
          horizontal="right"
        />
      </>
    );
  },
);

export default GagClauseCompliancePage;
